import {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
  useContext,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Flex,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Box,
  Heading,
  CloseButton,
  HStack,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import { AuthContext } from 'contexts/AuthContext';
import { ROLE_ADMIN } from 'variables/constants';
import Card from 'components/card/Card';

import QuoteLayout from './components/QuoteLayout';
import ActurisConfigBlocker from './components/ActurisConfigBlocker';
import {
  PolicyInfoStep,
  PropertyPolicyInfoStep,
  LocationStep,
  GeneralStep,
  AdditionalStep,
  VehicleStep,
  DriverStep,
  AssignmentStep,
  AutoCarriersStep,
  PropertyCarriersStep,
  AutoValidationsStep,
  PropertyValidationsStep,
  QuoteStep as QuoteSummaryStep,
  PropertyQuoteStep,
  CombinedQuoteStep,
} from './steps';
import {
  DiscrepancyModal,
  OrderReportsConsentModal,
} from './components/carriers';
import { EffectiveDateWarningBanner } from './components';
import { useOpenQuotes } from 'hooks/useOpenQuotes';
import {
  useQuoteFormState,
  ValidationSchemaProvider,
  EffectiveDateProvider,
  ProductTargetProvider,
  useDriverReports,
  useActurisConfigStatus,
  useAnswerCodes,
  QuoteLockProvider,
  isQuoteReadOnly,
  useValidationsStepData,
} from './hooks';
import {
  apiGetAutoQuotePolicyInfo,
  apiGetAutoQuoteVehicles,
  apiGetAutoQuoteDrivers,
  apiGetAutoQuoteAssignment,
  apiGetAutoQuoteCarriers,
  apiGetAutoQuoteValidations,
  apiGetAutoQuoteSummary,
  apiGetPropertyQuotePolicyInfo,
  apiGetPropertyQuoteLocations,
  apiGetPropertyQuoteGeneral,
  apiGetPropertyQuoteAdditional,
  apiGetPropertyQuoteCarriers,
  apiGetPropertyQuoteValidations,
  apiGetPropertyQuoteSummary,
  apiCreateQuoteConsent,
  type DashReportDriver,
  type CarriersData,
  type AutoCarriersData,
  type PropertyCarriersData,
  type CarrierQuote,
  type Driver,
  type DriversData,
  type LocationsData,
  type GeneralData,
  type AdditionalData,
  type AutoValidationsData,
  type PropertyValidationsData,
  type QuoteSummaryData,
  type PropertyQuoteSummaryData,
  type QuoteLock,
} from 'api';
import {
  LOB_AUTO,
  LOB_PROPERTY,
  getStepsForLOB,
  getDefaultStep,
  CONSENT_TYPE_PERSONAL_INFO_AUTH,
  isEffectiveDateWarningCode,
  type QuoteLOB,
  type QuoteStep,
  type QuoteNavigationState,
} from './constants';
import { removeInvalidDriversFromAssignments } from './utils/driverAssignment';

const AUTO_STEP_API_MAP: Record<string, (id: number) => Promise<any>> = {
  'policy-info': apiGetAutoQuotePolicyInfo,
  vehicle: apiGetAutoQuoteVehicles,
  drivers: apiGetAutoQuoteDrivers,
  assignment: apiGetAutoQuoteAssignment,
  carriers: apiGetAutoQuoteCarriers,
  validations: apiGetAutoQuoteValidations,
  quote: apiGetAutoQuoteSummary,
};

const PROPERTY_STEP_API_MAP: Record<string, (id: number) => Promise<any>> = {
  'policy-info': apiGetPropertyQuotePolicyInfo,
  locations: apiGetPropertyQuoteLocations,
  general: apiGetPropertyQuoteGeneral,
  additional: apiGetPropertyQuoteAdditional,
  carriers: apiGetPropertyQuoteCarriers,
  validations: apiGetPropertyQuoteValidations,
  quote: apiGetPropertyQuoteSummary,
};

const getApiForStep = (lob: QuoteLOB, step: string) => {
  const map = lob === LOB_AUTO ? AUTO_STEP_API_MAP : PROPERTY_STEP_API_MAP;
  return map[step] || apiGetAutoQuotePolicyInfo;
};

interface EffectiveDateWarningProps {
  onNavigate: () => void;
  showNavigateButton: boolean;
}

const EffectiveDateWarning = ({
  onNavigate,
  showNavigateButton,
}: EffectiveDateWarningProps) => {
  const { warning } = useAnswerCodes();

  if (!warning || !isEffectiveDateWarningCode(warning.code)) {
    return null;
  }

  return (
    <EffectiveDateWarningBanner
      requestedDate={warning.requestedDate}
      validFrom={warning.validFrom}
      validTo={warning.validTo}
      onNavigate={onNavigate}
      showNavigateButton={showNavigateButton}
      warningCode={warning.code}
    />
  );
};

const OpenedQuoteView = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === ROLE_ADMIN;

  const {
    canAccess,
    missingFields,
    message,
    isLoading: isConfigLoading,
  } = useActurisConfigStatus();

  const { contactRef, lob, quoteId, step } = useParams<{
    contactRef: string;
    lob: string;
    quoteId: string;
    step: string;
  }>();

  const customerId = contactRef || '';
  const quoteIdNum = Number(quoteId);
  const quoteLob = (lob as QuoteLOB) || LOB_AUTO;
  const activeStepKey = step || getDefaultStep(quoteLob);

  const apiFn = getApiForStep(quoteLob, activeStepKey);

  const {
    data: stepData,
    isLoading,
    isFetching: isFetchingStepData,
    dataUpdatedAt: stepDataUpdatedAt,
    error,
    refetch: refetchStepData,
  } = useQuery({
    queryKey: ['marketplace-quote', quoteIdNum, activeStepKey],
    queryFn: () => apiFn(quoteIdNum),
    enabled: !!quoteIdNum,
    staleTime: 0,
    refetchInterval: (query) => {
      if (activeStepKey !== 'carriers') return false;
      const data = query.state.data as CarriersData | undefined;
      const isPolling =
        data?.rfq_status === 'submitted' ||
        data?.rfq_status === 'polling' ||
        data?.risk_details_sync?.status === 'pending' ||
        data?.risk_details_sync?.queued;
      return isPolling ? 2000 : false;
    },
  });

  const { data: carriersState } = useQuery({
    queryKey: ['marketplace-quote', quoteIdNum, 'carriers-state'],
    queryFn: () =>
      quoteLob === LOB_AUTO
        ? apiGetAutoQuoteCarriers(quoteIdNum)
        : apiGetPropertyQuoteCarriers(quoteIdNum),
    enabled: !!quoteIdNum && activeStepKey !== 'carriers',
    staleTime: 30 * 1000,
  });

  const carriersData =
    activeStepKey === 'carriers' ? (stepData as CarriersData) : carriersState;
  const hasCarrierSelected = carriersData?.selected_carrier_quote_id != null;
  const isValidForRfq = carriersData?.is_valid_for_rfq ?? true;
  const validationErrorCount = carriersData?.validation_errors?.length ?? 0;
  const rfqInProgress =
    carriersData?.rfq_status === 'submitted' ||
    carriersData?.rfq_status === 'polling';
  const hasQuotes = carriersData?.rfq_status === 'complete';

  const prevRfqInProgressRef = useRef(rfqInProgress);
  useEffect(() => {
    if (prevRfqInProgressRef.current && !rfqInProgress) {
      queryClient.invalidateQueries({
        queryKey: ['market-credentials', quoteIdNum],
      });
    }
    prevRfqInProgressRef.current = rfqInProgress;
  }, [rfqInProgress, quoteIdNum, queryClient]);

  const quoteLock: QuoteLock | null =
    (stepData as { lock?: QuoteLock })?.lock ?? carriersData?.lock ?? null;
  const isReadOnly = isQuoteReadOnly(quoteLock);

  const { data: propertyPolicyInfoData, refetch: refetchPropertyPolicyInfo } =
    useQuery({
      queryKey: ['marketplace-quote', quoteIdNum, 'policy-info-property'],
      queryFn: () => apiGetPropertyQuotePolicyInfo(quoteIdNum),
      enabled:
        !!quoteIdNum &&
        quoteLob === LOB_PROPERTY &&
        (activeStepKey === 'additional' || activeStepKey === 'locations'),
      staleTime: 30 * 1000,
    });

  const navigationStateBase = {
    isValidForRfq,
    hasCarrierSelected,
    validationErrorCount,
    rfqInProgress,
  };

  const steps: QuoteStep[] = getStepsForLOB(quoteLob);

  const { quotes: openQuotes, clearQuotes } = useOpenQuotes(customerId);

  const {
    hasPendingChanges,
    isSaving,
    quotesWereReset,
    dismissQuotesResetBanner,
    handlePolicyInfoUpdate,
    handleVehiclesUpdate,
    handleDriversUpdate,
    handleLocationsUpdate,
    handleSave,
    getPendingVehicles,
    updatePolicyInfoMutation,
  } = useQuoteFormState({ quoteId: quoteIdNum, activeStepKey });

  const { data: vehiclesData } = useQuery({
    queryKey: ['marketplace-quote', quoteIdNum, 'vehicle'],
    queryFn: () => apiGetAutoQuoteVehicles(quoteIdNum),
    enabled:
      !!quoteIdNum &&
      (activeStepKey === 'drivers' || activeStepKey === 'assignment'),
    staleTime: 30 * 1000,
  });

  const { data: driversDataForAssignment } = useQuery({
    queryKey: ['marketplace-quote', quoteIdNum, 'drivers'],
    queryFn: () => apiGetAutoQuoteDrivers(quoteIdNum),
    enabled: !!quoteIdNum && activeStepKey === 'assignment',
    staleTime: 30 * 1000,
  });

  const validationsStepData = useValidationsStepData({
    quoteId: quoteIdNum,
    quoteLob,
    enabled: !!quoteIdNum && activeStepKey === 'validations',
  });

  const toast = useToast();
  const {
    isOpen: isDiscrepancyModalOpen,
    onOpen: openDiscrepancyModal,
    onClose: closeDiscrepancyModal,
  } = useDisclosure();

  const handleValidationComplete = useCallback(() => {
    if (activeStepKey !== 'carriers') {
      return;
    }
    navigate(
      `/admin/quote-marketplace/customer/${customerId}/${lob}/${quoteId}/validations`,
    );
  }, [navigate, customerId, lob, quoteId, activeStepKey]);
  const {
    isOpen: isConsentModalOpen,
    onOpen: openConsentModal,
    onClose: closeConsentModal,
  } = useDisclosure();
  const [isConsentSaving, setIsConsentSaving] = useState(false);

  const { data: driversDataForConsent } = useQuery({
    queryKey: ['marketplace-quote', quoteIdNum, 'drivers-for-consent'],
    queryFn: () => apiGetAutoQuoteDrivers(quoteIdNum),
    enabled: !!quoteIdNum && activeStepKey === 'carriers' && hasCarrierSelected,
    staleTime: 60 * 1000,
  });

  const selectedCarrierName =
    carriersData?.carrier_quotes?.find(
      (cq: CarrierQuote) => cq.id === carriersData?.selected_carrier_quote_id,
    )?.carrier_name || '';

  const { driversWithLicense, driversWithoutLicense } = useMemo(() => {
    if (!driversDataForConsent?.drivers) {
      return { driversWithLicense: [], driversWithoutLicense: [] };
    }

    const withLicense: { id: number; name: string }[] = [];
    const withoutLicense: { id: number; name: string }[] = [];

    driversDataForConsent.drivers.forEach((d: Driver) => {
      const driverInfo = {
        id: d.id,
        name:
          `${d.personal_info?.first_name || ''} ${d.personal_info?.last_name || ''}`.trim() ||
          `Driver ${d.id}`,
      };

      if (d.personal_info?.license_number?.trim()) {
        withLicense.push(driverInfo);
      } else {
        withoutLicense.push(driverInfo);
      }
    });

    return {
      driversWithLicense: withLicense,
      driversWithoutLicense: withoutLicense,
    };
  }, [driversDataForConsent]);

  const handleApplyChangesComplete = useCallback(
    (redirectTo?: string) => {
      closeDiscrepancyModal();
      refetchStepData();

      if (redirectTo === 'drivers') {
        navigate(
          `/admin/quote-marketplace/customer/${customerId}/${lob}/${quoteId}/drivers?highlight=incidents`,
        );
        toast({
          title: 'Data applied from reports',
          description:
            'Please review driver incidents and fill in required fields.',
          status: 'info',
          duration: 7000,
          isClosable: true,
          position: 'top-right',
        });
      } else {
        toast({
          title: 'Changes applied',
          description:
            'Please request new quotes with the updated information.',
          status: 'info',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      }
    },
    [
      closeDiscrepancyModal,
      refetchStepData,
      navigate,
      customerId,
      lob,
      quoteId,
      toast,
    ],
  );

  const handleSkipChangesComplete = useCallback(() => {
    closeDiscrepancyModal();
    navigate(
      `/admin/quote-marketplace/customer/${customerId}/${lob}/${quoteId}/validations`,
    );
  }, [closeDiscrepancyModal, navigate, customerId, lob, quoteId]);

  const handleProceedWithoutMVRComplete = useCallback(() => {
    refetchStepData();
    navigate(
      `/admin/quote-marketplace/customer/${customerId}/${lob}/${quoteId}/validations`,
    );
    toast({
      title: 'Proceeding without MVR',
      description:
        'MVR verification skipped. Please review validation reports.',
      status: 'info',
      duration: 5000,
      isClosable: true,
      position: 'top-right',
    });
  }, [refetchStepData, navigate, customerId, lob, quoteId, toast]);

  const handleValidationError = useCallback(
    (error: string) => {
      toast({
        title: 'Report ordering failed',
        description: error,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    },
    [toast],
  );

  const driverReports = useDriverReports({
    quoteId: quoteIdNum,
    enabled:
      quoteLob === LOB_AUTO &&
      hasCarrierSelected &&
      (activeStepKey === 'carriers' || activeStepKey === 'validations'),
    onComplete: handleValidationComplete,
    onApplyChangesComplete: handleApplyChangesComplete,
    onSkipChangesComplete: handleSkipChangesComplete,
    onProceedWithoutMVRComplete: handleProceedWithoutMVRComplete,
    onError: handleValidationError,
  });

  const hasValidDecision =
    driverReports.decision === 'skipped' ||
    driverReports.decision === 'applied' ||
    driverReports.decision === 'proceeded_without_mvr' ||
    driverReports.decision === 'skipped_no_license';

  const validationIncomplete =
    driverReports.status === 'in_progress' ||
    (driverReports.status === 'error' && !hasValidDecision) ||
    (driverReports.hasDiscrepancies && !driverReports.decision);

  const navigationState: QuoteNavigationState = {
    ...navigationStateBase,
    discrepancyPending: validationIncomplete,
  };

  useEffect(() => {
    if (
      (activeStepKey === 'validations' || activeStepKey === 'quote') &&
      validationIncomplete
    ) {
      navigate(
        `/admin/quote-marketplace/customer/${customerId}/${lob}/${quoteId}/carriers`,
      );
    }
  }, [activeStepKey, validationIncomplete, navigate, customerId, lob, quoteId]);

  const hasMvrError =
    driverReports.errorDetails !== null && driverReports.canRetry;

  const handleProceedClick = useCallback(() => {
    if (quoteLob === LOB_PROPERTY) {
      navigate(
        `/admin/quote-marketplace/customer/${customerId}/${lob}/${quoteId}/validations`,
      );
      return;
    }

    if (driverReports.hasDiscrepancies && !driverReports.decision) {
      openDiscrepancyModal();
      return;
    }

    if (hasMvrError) {
      driverReports.proceedWithoutMVR();
      return;
    }

    if (driverReports.status === 'complete') {
      navigate(
        `/admin/quote-marketplace/customer/${customerId}/${lob}/${quoteId}/validations`,
      );
      return;
    }

    if (carriersData?.has_reports_consent) {
      driverReports.startValidation();
    } else {
      openConsentModal();
    }
  }, [
    quoteLob,
    carriersData,
    driverReports,
    hasMvrError,
    openConsentModal,
    openDiscrepancyModal,
    navigate,
    customerId,
    lob,
    quoteId,
  ]);

  const handleConsentConfirm = useCallback(
    async (method: number) => {
      setIsConsentSaving(true);
      try {
        if (driversWithLicense.length > 0) {
          await apiCreateQuoteConsent(quoteIdNum, {
            type: CONSENT_TYPE_PERSONAL_INFO_AUTH,
            method,
          });
        }
        closeConsentModal();
        driverReports.startValidation();
      } catch {
        toast({
          title: 'Failed to save consent',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      } finally {
        setIsConsentSaving(false);
      }
    },
    [
      quoteIdNum,
      driversWithLicense.length,
      closeConsentModal,
      driverReports,
      toast,
    ],
  );

  const hasShownInitialModal = useRef(false);

  useEffect(() => {
    if (
      quoteLock &&
      !isReadOnly &&
      driverReports.status === 'complete' &&
      driverReports.hasDiscrepancies &&
      !driverReports.decision &&
      !hasShownInitialModal.current
    ) {
      hasShownInitialModal.current = true;
      openDiscrepancyModal();
    }
  }, [
    quoteLock,
    isReadOnly,
    driverReports.status,
    driverReports.hasDiscrepancies,
    driverReports.decision,
    openDiscrepancyModal,
  ]);

  useEffect(() => {
    if (driverReports.status !== 'complete') {
      hasShownInitialModal.current = false;
    }
  }, [driverReports.status]);

  const handleViewDriver = useCallback(
    (driverId: number, discrepancyIndices: number[]) => {
      closeDiscrepancyModal();
      if (!isReadOnly) {
        discrepancyIndices.forEach((index) => {
          driverReports.markViewed(index);
        });
      }
      navigate(
        `/admin/quote-marketplace/customer/${customerId}/${lob}/${quoteId}/drivers?highlight=driver-${driverId}-incidents`,
      );
    },
    [
      isReadOnly,
      driverReports,
      closeDiscrepancyModal,
      navigate,
      customerId,
      lob,
      quoteId,
    ],
  );

  const handleStepChange = (stepKey: string) => {
    navigate(
      `/admin/quote-marketplace/customer/${customerId}/${lob}/${quoteId}/${stepKey}`,
    );
  };

  const [getQuotesRequestedAt, setGetQuotesRequestedAt] = useState<
    number | null
  >(null);

  const shouldAutoGetQuotes =
    getQuotesRequestedAt !== null &&
    activeStepKey === 'carriers' &&
    !isFetchingStepData &&
    stepDataUpdatedAt >= getQuotesRequestedAt;

  const handleAssignmentGetQuotes = () => {
    setGetQuotesRequestedAt(Date.now());
    handleStepChange('carriers');
  };

  const handleQuoteChange = (newQuoteId: number, newLob: QuoteLOB) => {
    const newStep = getDefaultStep(newLob);
    navigate(
      `/admin/quote-marketplace/customer/${customerId}/${newLob}/${newQuoteId}/${newStep}`,
    );
  };

  const handleExit = () => {
    clearQuotes();
    navigate(`/admin/quote-marketplace/customer/${customerId}`);
  };

  const activeStep = steps.find((s) => s.key === activeStepKey);

  const handleDriversChange = useCallback(
    (drivers: DashReportDriver[]) => {
      handlePolicyInfoUpdate({
        full_history_dash_dash_report_drivers: drivers,
      });
    },
    [handlePolicyInfoUpdate],
  );

  const handleInsuranceHistoryChange = useCallback(
    (data: Record<string, unknown>) => {
      const updatedInsuranceHistory = {
        ...(propertyPolicyInfoData?.policy_info?.insurance_history || {}),
        ...data,
      };
      updatePolicyInfoMutation.mutate(
        { insurance_history: updatedInsuranceHistory },
        { onSuccess: () => refetchPropertyPolicyInfo() },
      );
    },
    [
      updatePolicyInfoMutation,
      propertyPolicyInfoData,
      refetchPropertyPolicyInfo,
    ],
  );

  const handleDriversUpdateWithAssignmentCleanup = useCallback(
    (drivers: Driver[], quoteProvince: number) => {
      handleDriversUpdate(drivers);

      const currentVehicles =
        getPendingVehicles() ?? vehiclesData?.vehicles ?? [];
      if (currentVehicles.length === 0) return;

      const { vehicles: updatedVehicles, hasChanges } =
        removeInvalidDriversFromAssignments(
          currentVehicles,
          drivers,
          quoteProvince,
        );

      if (hasChanges) {
        handleVehiclesUpdate(updatedVehicles);
      }
    },
    [
      handleDriversUpdate,
      handleVehiclesUpdate,
      getPendingVehicles,
      vehiclesData?.vehicles,
    ],
  );

  const handleDriversUpdateForDriverStep = useCallback(
    (drivers: Driver[]) => {
      const driversData = stepData as DriversData | undefined;
      handleDriversUpdateWithAssignmentCleanup(
        drivers,
        driversData?.province || 1,
      );
    },
    [handleDriversUpdateWithAssignmentCleanup, stepData],
  );

  const handleDriversUpdateForAssignmentStep = useCallback(
    (drivers: Driver[]) => {
      handleDriversUpdateWithAssignmentCleanup(
        drivers,
        driversDataForAssignment?.province || 1,
      );
    },
    [handleDriversUpdateWithAssignmentCleanup, driversDataForAssignment],
  );

  const renderContent = () => {
    if (rfqInProgress && activeStepKey !== 'carriers') {
      return (
        <Flex justify="center" align="center" minH="300px">
          <Box
            bg="white"
            p={8}
            borderRadius="lg"
            boxShadow="sm"
            border="1px solid"
            borderColor="gray.200"
            textAlign="center"
            maxW="md"
          >
            <Spinner size="lg" color="blue.500" thickness="3px" mb={4} />
            <Heading size="md" mb={2}>
              Fetching Quotes
            </Heading>
            <Text color="gray.600" mb={6}>
              Please wait while carrier quotes are being retrieved. You can
              monitor progress on the Carriers tab.
            </Text>
            <Button
              colorScheme="yellow"
              onClick={() => handleStepChange('carriers')}
              size="md"
            >
              View Progress
            </Button>
          </Box>
        </Flex>
      );
    }

    if (isLoading) {
      return (
        <Flex justify="center" align="center" minH="200px">
          <Spinner size="lg" />
          <Text ml={4}>Loading...</Text>
        </Flex>
      );
    }

    if (error) {
      return (
        <Alert status="error">
          <AlertIcon />
          Failed to load quote data
        </Alert>
      );
    }

    if (!stepData) {
      return (
        <Alert status="error">
          <AlertIcon />
          Failed to load quote data
        </Alert>
      );
    }

    if (activeStepKey === 'policy-info') {
      if (quoteLob === LOB_PROPERTY) {
        return (
          <PropertyPolicyInfoStep
            data={stepData}
            onUpdate={handlePolicyInfoUpdate}
            isUpdating={isSaving}
          />
        );
      }
      return (
        <PolicyInfoStep
          data={stepData}
          onUpdate={handlePolicyInfoUpdate}
          onDriversChange={handleDriversChange}
          onVehiclesPopulated={() => {
            queryClient.invalidateQueries({
              queryKey: ['marketplace-quote', quoteIdNum, 'vehicle'],
            });
            queryClient.invalidateQueries({
              queryKey: ['marketplace-quote', quoteIdNum, 'drivers'],
            });
          }}
          isUpdating={isSaving}
        />
      );
    }

    if (activeStepKey === 'vehicle') {
      return (
        <VehicleStep
          data={stepData}
          quoteId={quoteIdNum}
          onUpdate={handleVehiclesUpdate}
          isUpdating={isSaving}
        />
      );
    }

    if (activeStepKey === 'locations') {
      return (
        <LocationStep
          data={stepData as LocationsData}
          quoteId={quoteIdNum}
          onUpdate={handleLocationsUpdate}
          isUpdating={isSaving}
          customerType={propertyPolicyInfoData?.policy_info?.customer_type}
        />
      );
    }

    if (activeStepKey === 'general') {
      return (
        <GeneralStep
          data={stepData as GeneralData}
          quoteId={quoteIdNum}
          onUpdate={handleLocationsUpdate}
          onSave={handleSave}
          isUpdating={isSaving}
        />
      );
    }

    if (activeStepKey === 'additional') {
      return (
        <AdditionalStep
          data={stepData as AdditionalData}
          onUpdate={handleLocationsUpdate}
          isUpdating={isSaving}
          insuranceHistory={
            propertyPolicyInfoData?.policy_info?.insurance_history
          }
          onInsuranceHistoryChange={handleInsuranceHistoryChange}
          province={propertyPolicyInfoData?.province}
        />
      );
    }

    if (activeStepKey === 'drivers') {
      return (
        <DriverStep
          data={stepData}
          vehicles={vehiclesData?.vehicles || []}
          effectiveDate={stepData?.effective_date || ''}
          discrepancies={driverReports.discrepancies}
          onUpdate={handleDriversUpdateForDriverStep}
          isUpdating={isSaving}
        />
      );
    }

    if (activeStepKey === 'assignment') {
      return (
        <AssignmentStep
          data={stepData}
          fullVehicles={vehiclesData?.vehicles || []}
          fullDrivers={driversDataForAssignment?.drivers}
          onUpdate={handleVehiclesUpdate}
          onDriversUpdate={handleDriversUpdateForAssignmentStep}
          isUpdating={isSaving}
        />
      );
    }

    if (activeStepKey === 'carriers') {
      if (quoteLob === LOB_PROPERTY) {
        const propertyData = stepData as PropertyCarriersData;
        return (
          <PropertyCarriersStep
            data={propertyData}
            quoteId={quoteIdNum}
            onNavigateToTab={handleStepChange}
            onRefetch={refetchStepData}
          />
        );
      }
      const autoData = stepData as AutoCarriersData;
      return (
        <AutoCarriersStep
          data={autoData}
          quoteId={quoteIdNum}
          onNavigateToTab={handleStepChange}
          onRefetch={refetchStepData}
          autoGetQuotes={shouldAutoGetQuotes}
          onAutoGetQuotesHandled={() => setGetQuotesRequestedAt(null)}
          driverReports={{
            isValidating: driverReports.isValidating,
            status: driverReports.status,
            errorDetails: driverReports.errorDetails,
            canRetry: driverReports.canRetry,
            retryValidation: driverReports.retryValidation,
          }}
        />
      );
    }

    if (activeStepKey === 'validations') {
      if (validationsStepData.isLoading) {
        return (
          <Flex justify="center" align="center" minH="200px">
            <Spinner size="lg" />
            <Text ml={4}>Loading...</Text>
          </Flex>
        );
      }
      if (!validationsStepData.hasCarrierSelected) {
        return (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Text>
              Please select a carrier quote first before viewing validations.
            </Text>
            <Button
              size="sm"
              colorScheme="orange"
              variant="solid"
              ml={4}
              onClick={() => handleStepChange('carriers')}
            >
              Go to Carriers
            </Button>
          </Alert>
        );
      }
      if (validationsStepData.error) {
        return (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Text>
              Could not load validations. The carrier selection may have been
              cleared by a recent change — please re-select a carrier quote and
              try again.
            </Text>
            <Button
              size="sm"
              colorScheme="red"
              variant="solid"
              ml={4}
              onClick={() => handleStepChange('carriers')}
            >
              Go to Carriers
            </Button>
          </Alert>
        );
      }
      if (quoteLob === LOB_PROPERTY) {
        return (
          <PropertyValidationsStep
            data={stepData as PropertyValidationsData}
            validations={validationsStepData.validations}
          />
        );
      }
      return (
        <AutoValidationsStep
          data={stepData as AutoValidationsData}
          quoteId={quoteIdNum}
          driverReports={driverReports}
          onOpenDiscrepancyModal={openDiscrepancyModal}
          validations={validationsStepData.validations}
        />
      );
    }

    if (activeStepKey === 'quote') {
      const summaryData = stepData as
        | QuoteSummaryData
        | PropertyQuoteSummaryData;
      if (summaryData.group_id) {
        return <CombinedQuoteStep groupId={summaryData.group_id} />;
      }

      if (quoteLob === LOB_PROPERTY) {
        return (
          <PropertyQuoteStep
            data={stepData as PropertyQuoteSummaryData}
            quoteId={quoteIdNum}
            lob={quoteLob}
          />
        );
      }
      return (
        <QuoteSummaryStep
          data={stepData as QuoteSummaryData}
          quoteId={quoteIdNum}
          lob={quoteLob}
        />
      );
    }

    return (
      <Text fontSize="lg" fontWeight="600" mb={4}>
        {activeStep?.label} - Quote #{stepData.id}
      </Text>
    );
  };

  if (!isConfigLoading && !canAccess) {
    return (
      <Card p={6}>
        <ActurisConfigBlocker
          missingFields={missingFields}
          message={message}
          isAdmin={isAdmin}
          userId={user?.id}
        />
      </Card>
    );
  }

  return (
    <ProductTargetProvider
      productTarget={quoteLob === LOB_AUTO ? 'auto' : 'habitational'}
    >
      <EffectiveDateProvider effectiveDate={stepData?.effective_date || null}>
        <ValidationSchemaProvider quoteId={quoteIdNum}>
          <QuoteLockProvider lock={quoteLock}>
            <QuoteLayout
              quoteId={quoteIdNum}
              lob={quoteLob}
              steps={steps}
              navigationState={navigationState}
              openQuotes={openQuotes}
              activeStepKey={activeStepKey}
              onStepChange={handleStepChange}
              onQuoteChange={handleQuoteChange}
              onExit={handleExit}
              onSave={handleSave}
              isSaving={isSaving}
              hasPendingChanges={hasPendingChanges}
              carriersProceed={{
                onProceed: handleProceedClick,
                isLoading:
                  driverReports.isValidating ||
                  driverReports.isProceedingWithoutMVR ||
                  isConsentSaving,
                hasMvrError,
              }}
              assignmentGetQuotes={
                quoteLob === LOB_AUTO && !hasQuotes
                  ? {
                      onGetQuotes: handleAssignmentGetQuotes,
                      isDisabled: rfqInProgress,
                    }
                  : undefined
              }
            >
              {quotesWereReset && (
                <Alert status="info" mb={4} borderRadius="md">
                  <AlertIcon />
                  <HStack flex={1} justify="space-between">
                    <Text>
                      Carrier quotes have been reset due to changes in your
                      submission. Please request new quotes when ready.
                    </Text>
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        variant="solid"
                        onClick={() => {
                          dismissQuotesResetBanner();
                          handleStepChange('carriers');
                        }}
                      >
                        Go to Carriers
                      </Button>
                      <CloseButton onClick={dismissQuotesResetBanner} />
                    </HStack>
                  </HStack>
                </Alert>
              )}
              <EffectiveDateWarning
                onNavigate={() => handleStepChange('policy-info')}
                showNavigateButton={activeStepKey !== 'policy-info'}
              />
              {renderContent()}
            </QuoteLayout>

            <DiscrepancyModal
              isOpen={isDiscrepancyModalOpen}
              onClose={closeDiscrepancyModal}
              onApplyChanges={driverReports.applyChanges}
              onSkipChanges={driverReports.skipChanges}
              onViewDriver={handleViewDriver}
              discrepancies={driverReports.discrepancies}
              viewedIndices={driverReports.viewedIndices}
              isApplying={driverReports.isApplying}
              isSkipping={driverReports.isSkipping}
              decision={driverReports.decision}
              isReviewMode={activeStepKey === 'validations'}
            />

            <OrderReportsConsentModal
              isOpen={isConsentModalOpen}
              onClose={closeConsentModal}
              onConfirm={handleConsentConfirm}
              isLoading={isConsentSaving}
              carrierName={selectedCarrierName}
              driversWithLicense={driversWithLicense}
              driversWithoutLicense={driversWithoutLicense}
            />
          </QuoteLockProvider>
        </ValidationSchemaProvider>
      </EffectiveDateProvider>
    </ProductTargetProvider>
  );
};

export default OpenedQuoteView;
